import React from 'react';
import { processContent } from 'latex-content-renderer';

const LATEX_PATTERN = /(\$\$[\s\S]*?\$\$|\$[^\n$]+\$|\\\([\s\S]*?\\\)|\\\[[\s\S]*?\\\]|\\begin\{|\\frac\{|\\sqrt\{|\\sum|\\int|\\alpha|\\beta|\\gamma|\\theta|\\pi|\\times|\\cdot|\\leq|\\geq|\\vec\{|\\overrightarrow\{|\\boldsymbol\{|\\bm\{)/;
const MATH_LIKE_PATTERN = /([A-Za-z]\w*_[A-Za-z0-9]+|[A-Za-z0-9]+\^[A-Za-z0-9+-]+|[A-Za-z][A-Za-z0-9_]*\s*\/\s*[A-Za-z][A-Za-z0-9_]*|[₀-₉₊₋₌₍₎ₐₑₒₓₔₕₖₗₘₙₚₛₜ]+|[⁰¹²³⁴⁵⁶⁷⁸⁹⁺⁻⁼⁽⁾ⁿⁱ]+|[=<>]|≤|≥|≈|≠|→|∑|∫|√)/;

const UNICODE_SUPERSCRIPT_MAP = {
	'⁰': '0',
	'¹': '1',
	'²': '2',
	'³': '3',
	'⁴': '4',
	'⁵': '5',
	'⁶': '6',
	'⁷': '7',
	'⁸': '8',
	'⁹': '9',
	'⁺': '+',
	'⁻': '-',
	'⁼': '=',
	'⁽': '(',
	'⁾': ')',
	'ⁿ': 'n',
	'ⁱ': 'i'
};

const UNICODE_SUBSCRIPT_MAP = {
	'₀': '0',
	'₁': '1',
	'₂': '2',
	'₃': '3',
	'₄': '4',
	'₅': '5',
	'₆': '6',
	'₇': '7',
	'₈': '8',
	'₉': '9',
	'₊': '+',
	'₋': '-',
	'₌': '=',
	'₍': '(',
	'₎': ')',
	'ₐ': 'a',
	'ₑ': 'e',
	'ₒ': 'o',
	'ₓ': 'x',
	'ₔ': 'schwa',
	'ₕ': 'h',
	'ₖ': 'k',
	'ₗ': 'l',
	'ₘ': 'm',
	'ₙ': 'n',
	'ₚ': 'p',
	'ₛ': 's',
	'ₜ': 't'
};

const hasLatexContent = (value) => LATEX_PATTERN.test(String(value || ''));
const hasMathLikeContent = (value) => MATH_LIKE_PATTERN.test(String(value || ''));

const hasSubstantialNaturalLanguage = (value) => {
	const text = String(value || '');
	const words = text.match(/[A-Za-zÀ-ÿ'’-]{3,}/g) || [];
	// Treat content with several long words as natural prose, not pure formula.
	return words.length >= 3;
};

const hasOddUnescapedDollarCount = (value) => {
	const text = String(value || '');
	let count = 0;
	for (let i = 0; i < text.length; i++) {
		if (text[i] === '$' && (i === 0 || text[i - 1] !== '\\')) count++;
	}
	return count % 2 === 1;
};

const stripMarkdownArtifacts = (input) => {
	let text = String(input || '');

	// Remove common markdown wrappers that should not be displayed in node labels.
	text = text.replace(/\*\*([^*\n]+)\*\*/g, '$1');
	text = text.replace(/__([^_\n]+)__/g, '$1');
	text = text.replace(/`([^`\n]+)`/g, '$1');

	// Remove leaked parser diagnostics that may appear in generated node text.
	text = text.replace(/Double\s+exponent\s*:?\s*use\s+braces\s+to\s+clarify\.?/gi, '').trim();

	// Fix malformed \text{...} blocks where the math closing delimiter \) or \] was placed
	// inside the \text{} argument instead of closing it first.
	// Works for plain content and content with embedded LaTeX commands (\text{m \cdot g\) etc.).
	// \text{km/h\)  ->  \text{km/h}\)
	// \text{km/h\]  ->  \text{km/h}\]
	text = text.replace(/\\text\{([^}]*?)\\([)\]])/g, '\\text{$1}\\$2');

	// Also handle multiple \text{} args on the same expression: run twice to catch adjacent cases.
	text = text.replace(/\\text\{([^}]*?)\\([)\]])/g, '\\text{$1}\\$2');

	return text;
};

const normalizeDoubleExponentNotation = (input) => {
	let text = String(input || '');

	// Fix common malformed exponent chains from OCR/LLM output.
	// Examples: x^a^b -> x^{a^{b}}, x^{a}^b -> x^{{a}^{b}}
	text = text.replace(/([A-Za-z0-9)\]}])\^\{([^{}]+)\}\^([A-Za-z0-9]+)/g, '$1^{{$2}^{$3}}');
	text = text.replace(/([A-Za-z0-9)\]}])\^([A-Za-z0-9]+)\^([A-Za-z0-9]+)/g, '$1^{$2^{$3}}');

	return text;
};

const hasVectorArtifactHint = (value) => /(?:[-—–]\s*){1,}>|→|\u20D7|\bvec\{|\boverrightarrow\{|∑|Σ/.test(String(value || ''));

const toCodePointPreview = (value, max = 180) =>
	Array.from(String(value || ''))
		.slice(0, max)
		.map((char) => `U+${char.codePointAt(0).toString(16).toUpperCase().padStart(4, '0')}`)
		.join(' ');

const isMathDebugEnabled = () => {
	if (typeof window === 'undefined') return false;

	try {
		const localStorageFlag = window.localStorage?.getItem('PLYNOTE_MATH_DEBUG');
		if (localStorageFlag === '1' || localStorageFlag === 'true') return true;
	} catch {
		// Ignore localStorage access issues.
	}

	const queryFlag = /[?&]mathDebug=(1|true)\b/i.test(window.location?.search || '');
	if (queryFlag) return true;

	return process.env.NODE_ENV !== 'production';
};

const cleanRawMathInput = (input) => {
	let text = stripMarkdownArtifacts(input);

	// Normalize compatibility forms from PDFs/Docs and remove invisible separators.
	text = text
		.normalize('NFKC')
		.replace(/[\u200B-\u200D\u2060\uFEFF]/g, '')
		.replace(/\u00A0/g, ' ')
		.replace(/\u00AD/g, '')
		.replace(/\u202F/g, ' ')
		.replace(/[\u2028\u2029]/g, '\n');

	// Normalize combining vector arrow notation: F⃗ -> \vec{F}
	text = text.replace(/([A-Za-z])\u20D7/g, (_match, symbol) => `\\vec{${symbol}}`);

	// Collapse duplicated backslashes before known math commands: \\\\vec -> \\vec
	text = text.replace(/\\{2,}(?=(?:vec|overrightarrow|sum|prod|frac|sqrt|alpha|beta|gamma|theta|pi|int|cdot|times|leq|geq|boldsymbol|bm)\b)/g, '\\');

	// Normalize shorthand bold command to a canonical command supported by MathJax config.
	text = text.replace(/\\bm\s*\{/g, '\\boldsymbol{');

	return text;
};

const convertUnicodeScriptSequence = (sequence, map) =>
	Array.from(sequence)
		.map((char) => map[char] || char)
		.join('');

const interpretAsciiMathNotations = (input) => {
	let text = String(input || '');
	const VECTOR_SYMBOL_PATTERN = '([A-Za-z](?:_\\{[^}]+\\}|_[A-Za-z0-9]+)?)';

	// Normalize OCR arrow artifacts with irregular spacing/dashes: '-- ->', '--->', '- ->', '-  -  >'.
	text = text.replace(/(?:[-—–]\s*){1,}[-—–]?\s*>/g, '-->');

	// Normalize Unicode OCR vector arrows: '−−→', '--→', '-→' -> '-->'
	text = text.replace(/(?:[−—–\-]\s*){1,}→/g, '-->');

	// Sum of forces style OCR artifact: ∑-- >F = ma / ∑-->F = ma -> \sum \vec{F} = ma
	text = text.replace(
		new RegExp(`(∑|Σ|\\\\sum)\\s*--?>\\s*${VECTOR_SYMBOL_PATTERN}`, 'g'),
		(_match, _sigma, symbol) => `\\sum \\vec{${symbol}}`
	);

	// Vector segment style OCR artifact: -- >OM = ... -> \overrightarrow{OM} = ...
	text = text.replace(
		/(^|[\s(=+,:;])\s*--?>\s*([A-Za-z]{1,6})(?=\s*(?:=|,|\)|;|$))/g,
		(_match, prefix, symbol) => {
			if (symbol.length >= 2) {
				return `${prefix}\\overrightarrow{${symbol}}`;
			}
			return `${prefix}\\vec{${symbol}}`;
		}
	);

	// Compact form without leading separator at string start: -->OM = ...
	text = text.replace(/^--?>\s*([A-Za-z]{1,6})(?=\s*(?:=|,|\)|;|$))/, (_match, symbol) => {
		if (symbol.length >= 2) {
			return `\\overrightarrow{${symbol}}`;
		}
		return `\\vec{${symbol}}`;
	});

	// OCR often inserts spaces inside segment names: --> O M = ... => \overrightarrow{OM}
	text = text.replace(
		/(^|[\s(=+,:;])\s*--?>\s*([A-Za-z](?:\s+[A-Za-z]){1,4})(?=\s*(?:=|,|\)|;|$))/g,
		(_match, prefix, spacedSymbol) => `${prefix}\\overrightarrow{${spacedSymbol.replace(/\s+/g, '')}}`
	);

	// Recover missing backslash for common vector commands from OCR/model output.
	text = text.replace(/(^|[^\\])\bvec\s*\{\s*([A-Za-z])\s*\}/g, '$1\\vec{$2}');
	text = text.replace(/(^|[^\\])\boverrightarrow\s*\{\s*([A-Za-z]{2,6})\s*\}/g, '$1\\overrightarrow{$2}');

	// Convert unicode arrow before symbol to a vector when it appears in formula context: →F, =→OM
	text = text.replace(
		/(^|[\s(=+,:;])→\s*([A-Za-z]{1,6})(?=\s*(?:=|,|\)|;|$))/g,
		(_match, prefix, symbol) => {
			if (symbol.length >= 2) {
				return `${prefix}\\overrightarrow{${symbol}}`;
			}
			return `${prefix}\\vec{${symbol}}`;
		}
	);

	// OCR vector artifact in physics/maths: ∑-->F=ma => ∑ \vec{F}=ma
	text = text.replace(
		/(∑|\\sum)\s*[-—–]{1,}>\s*([A-Za-z](?:_[A-Za-z0-9]+)?)/g,
		(_match, sigma, variable) => `${sigma} \\vec{${variable}}`
	);

	// Generic vector marker before a symbol in expressions: =-->a , +-->F, (-->v)
	text = text.replace(
		new RegExp(`(^|[\\s(=+,:;])\\s*[-—–]{1,}>\\s*${VECTOR_SYMBOL_PATTERN}(?=\\b|\\s|[)=+\\-/*,;]|$)`, 'g'),
		(_match, prefix, variable) => `${prefix}\\vec{${variable}}`
	);

	// Functional mapping declaration from extraction artifacts: f : A --> B
	text = text.replace(
		/(\b[A-Za-z][A-Za-z0-9_]*)\s*:\s*([^\n;]+?)\s*[-—–]{1,}>\s*([^\n;]+)/g,
		(_match, fnName, domain, codomain) => `${fnName} : ${domain.trim()} → ${codomain.trim()}`
	);

	// Pointwise mapping style: x --> f(x) should be rendered as mapsto when target is an expression.
	text = text.replace(
		/(^|[\n;]\s*)([A-Za-z](?:_[A-Za-z0-9]+)?)\s*[-—–]{1,}>\s*([^\n;]+)/g,
		(_match, prefix, variable, targetRaw) => {
			const target = targetRaw.trim();
			const isExpression = /[()]/.test(target) || /[+*/^]/.test(target);
			return `${prefix}${variable} ${isExpression ? '↦' : '→'} ${target}`;
		}
	);

	return text;
};

const normalizeMathDelimiters = (input) => {
	let text = String(input || '');

	// Prefer MathJax canonical delimiters to avoid ambiguity with plain dollar signs.
	text = text.replace(/\$\$([\s\S]*?)\$\$/g, (_match, expr) => `\\[${expr}\\]`);

	// Convert inline $...$ (non-empty, same line) to \(...\)
	text = text.replace(/(^|[^\\])\$(?!\$)([^\n$]+?)\$(?!\$)/g, (_match, prefix, expr) => `${prefix}\\(${expr}\\)`);

	// If LaTeX commands are present without delimiters, wrap once for MathJax parsing.
	const hasMathDelimiters = /\$|\\\(|\\\[/.test(text);
	const hasBareLatexCommands = /(\\frac\{|\\sqrt\{|\\sum|\\int|\\vec\{|\\overrightarrow\{|\\boldsymbol\{|\\bm\{|\\alpha|\\beta|\\gamma|\\theta|\\pi)/.test(text);
	if (!hasMathDelimiters && hasBareLatexCommands && !hasSubstantialNaturalLanguage(text)) {
		text = `\\(${text}\\)`;
	}

	return text;
};

const ensureMathWrapped = (input) => {
	const text = String(input || '');
	if (!text.trim()) return text;

	const hasMathDelimiters = /\$|\\\(|\\\[/.test(text);
	if (hasMathDelimiters) return text;

	if ((hasMathLikeContent(text) || /(\\frac\{|\\sqrt\{|\\sum|\\int|\\vec\{|\\overrightarrow\{|\\boldsymbol\{|\\bm\{)/.test(text)) && !hasSubstantialNaturalLanguage(text)) {
		return `\\(${text}\\)`;
	}

	return text;
};

const normalizeRemainingLeftVectorArrows = (input) => {
	let text = String(input || '');

	// Convert residual left-arrow vector forms to proper LaTeX vectors rendered above symbols.
	// Examples: -> "→OM" => "\\overrightarrow{OM}", "→F_{ext}" => "\\vec{F}_{ext}"
	text = text.replace(/(^|[\s(=+,:;])→\s*([A-Za-z]{2,6})(?=\b|\s|[)=+\-/*,;]|$)/g, '$1\\overrightarrow{$2}');
	text = text.replace(/(^|[\s(=+,:;])→\s*([A-Za-z])(?:_\{([^}]+)\}|_([A-Za-z0-9]+))?(?=\b|\s|[)=+\-/*,;]|$)/g, (_match, prefix, base, subA, subB) => {
		const sub = subA || subB;
		if (sub) {
			return `${prefix}\\vec{${base}}_{${sub}}`;
		}
		return `${prefix}\\vec{${base}}`;
	});

	return text;
};

const normalizeExtractedMathText = (input) => {
	let text = cleanRawMathInput(input);

	// Normalize common OCR symbols and punctuation variants found in PDFs/Docs.
	text = text
		.replace(/[−–—]/g, '-')
		.replace(/[×⋅·]/g, ' \\cdot ')
		.replace(/[⟶⟹⇒]/g, ' → ')
		.replace(/⁄/g, '/');

	text = interpretAsciiMathNotations(text);

	// Normalize square-root notations frequently produced by OCR/LLM.
	// Supported forms: sqrt(...), sqrt token, √(...), √token, and \sqrt(...)
	// Canonical target: \sqrt{...}
	text = text.replace(/(^|[^\\])\\sqrt\s*\(([^()]+)\)/g, '$1\\sqrt{$2}');
	text = text.replace(/(^|[^\\])\bsqrt\s*\(([^()]+)\)/g, '$1\\sqrt{$2}');
	text = text.replace(/√\s*\(([^()]+)\)/g, '\\sqrt{$1}');
	text = text.replace(/(^|[^\\])\bsqrt\s+([A-Za-z0-9]+(?:_[A-Za-z0-9]+)?(?:\^[A-Za-z0-9]+)?)/g, '$1\\sqrt{$2}');
	text = text.replace(/√\s*([A-Za-z0-9]+(?:_[A-Za-z0-9]+)?(?:\^[A-Za-z0-9]+)?)/g, '\\sqrt{$1}');
	text = text.replace(/(^|[^\\])\\sqrt\s+([A-Za-z0-9]+(?:_[A-Za-z0-9]+)?(?:\^[A-Za-z0-9]+)?)/g, '$1\\sqrt{$2}');

	// Strong OCR repair for vector notation: ∑-->F=ma / Σ-->F=ma -> \sum \vec{F}=ma
	text = text.replace(/(∑|Σ)\s*[-—–]{1,}>\s*([A-Za-z](?:_\{[^}]+\}|_[A-Za-z0-9]+)?)/g, '\\sum \\vec{$2}');
	text = text.replace(/\\sum\s*[-—–]{1,}>\s*([A-Za-z](?:_\{[^}]+\}|_[A-Za-z0-9]+)?)/g, '\\sum \\vec{$1}');

	// Recover common LaTeX commands when OCR strips the leading backslash.
	text = text.replace(/(^|[^\\])\bfrac\s*\{/g, '$1\\frac{');
	text = text.replace(/(^|[^\\])\bsqrt\s*\{/g, '$1\\sqrt{');

	// Convert unicode superscript runs: x² -> x^{2}, e⁻ˣ (partial) -> e^{-x} when possible.
	text = text.replace(/([A-Za-z0-9)\]}])([⁰¹²³⁴⁵⁶⁷⁸⁹⁺⁻⁼⁽⁾ⁿⁱ]+)/g, (_match, base, sup) => {
		const exponent = convertUnicodeScriptSequence(sup, UNICODE_SUPERSCRIPT_MAP);
		return `${base}^{${exponent}}`;
	});

	// Convert unicode subscript runs: uₙ -> u_{n}, x₀ -> x_{0}.
	text = text.replace(/([A-Za-z0-9)\]}])([₀₁₂₃₄₅₆₇₈₉₊₋₌₍₎ₐₑₒₓₔₕₖₗₘₙₚₛₜ]+)/g, (_match, base, sub) => {
		const index = convertUnicodeScriptSequence(sub, UNICODE_SUBSCRIPT_MAP);
		return `${base}_{${index}}`;
	});

	text = normalizeDoubleExponentNotation(text);

	return text;
};

const repairBrokenLatexFragments = (input) => {
	let text = normalizeExtractedMathText(input);

	// Final OCR fallback for vector notations that survived previous normalization.
	text = text.replace(/--\s*>\s*([A-Za-z](?:_\{[^}]+\}|_[A-Za-z0-9]+)?)\s*=/g, (_match, symbol) => {
		if (symbol.length >= 2) {
			return `\\overrightarrow{${symbol}} =`;
		}
		return `\\vec{${symbol}} =`;
	});

	// Remove stray quotes that often appear around LaTeX commands in model output.
	text = text.replace(/["'`]\s*(\\[A-Za-z]+)/g, '$1');

	// Repair common OCR/LLM arrow corruption inside formulas.
	// Example: \frac{d--->OM}{dt} => \frac{d\\overrightarrow{OM}}{dt}
	text = text.replace(/d\s*[-—–]{2,}>\s*([A-Za-z]{1,8})/g, 'd\\overrightarrow{$1}');

	// Generic long-arrow fallback: --->, -->, -> become a readable arrow symbol.
	text = text.replace(/[-—–]{1,}>/g, '→');

	// Final pass: transform any remaining left-arrow vector notation into over-arrow notation.
	text = normalizeRemainingLeftVectorArrows(text);

	// If a LaTeX command exists with odd number of unescaped $, close the expression.
	if (/(\\frac\{|\\sqrt\{|\\sum|\\int|\\alpha|\\beta|\\gamma|\\theta|\\to|\\overrightarrow\{)/.test(text)) {
		if (hasOddUnescapedDollarCount(text)) {
			text = `${text}$`;
		}
	}

	// Final robust fix: balance unclosed { braces inside every \(...\) and \[...\] expression.
	// This catches any case where a LaTeX command like \text{, \frac{, \sqrt{ etc.
	// was left unclosed before the math closing delimiter.
	text = text.replace(/\\\([\s\S]*?\\\)|\\\[[\s\S]*?\\\]/g, (expr) => {
		let depth = 0;
		for (let i = 0; i < expr.length; i++) {
			if (expr[i] === '\\') { i++; continue; } // skip escaped char (\{ \} \) etc.)
			if (expr[i] === '{') depth++;
			else if (expr[i] === '}') depth = Math.max(0, depth - 1);
		}
		if (depth <= 0) return expr;
		// Insert missing closing braces just before the final \) or \]
		const closingMatch = expr.match(/(\\[)\]])$/);
		if (!closingMatch) return expr;
		const body = expr.slice(0, expr.length - closingMatch[0].length);
		return body + '}'.repeat(depth) + closingMatch[0];
	});

	return text;
};

const toLatexAtom = (atom) => {
	if (!atom) return atom;

	let next = atom;

	// Convert subscripts: F_ext -> F_{ext}
	next = next.replace(/([A-Za-z][A-Za-z0-9]*)_([A-Za-z0-9]+)/g, '$1_{$2}');

	// Convert powers: x^2 -> x^{2}
	next = next.replace(/([A-Za-z0-9}])\^([A-Za-z0-9+-]+)/g, '$1^{$2}');

	return next;
};

const autoLatexifyMathLikeText = (input) => {
	let text = normalizeExtractedMathText(input);

	// Convert fraction-like tokens often produced by PDF extraction: F_ext/B -> \frac{F_{ext}}{B}
	text = text.replace(
		/\b([A-Za-z][A-Za-z0-9_]*(?:\^[A-Za-z0-9+-]+)?)\s*\/\s*([A-Za-z][A-Za-z0-9_]*(?:\^[A-Za-z0-9+-]+)?)\b/g,
		(_match, left, right) => `$\\frac{${toLatexAtom(left)}}{${toLatexAtom(right)}}$`
	);

	// Convert remaining standalone subscript tokens.
	text = text.replace(
		/\b([A-Za-z][A-Za-z0-9]*)_([A-Za-z0-9]+)\b/g,
		(_match, base, sub) => `$${base}_{${sub}}$`
	);

	// Convert remaining standalone power tokens.
	text = text.replace(
		/\b([A-Za-z0-9]+)\^([A-Za-z0-9+-]+)\b/g,
		(_match, base, power) => `$${base}^{${power}}$`
	);

	return text;
};

const MathContentLayer = ({ content = '', className = '', style = {} }) => {
	const containerRef = React.useRef(null);
	const safeContent = String(content || '');
	const cleanedInput = cleanRawMathInput(safeContent);

	const normalizedContent = hasLatexContent(cleanedInput)
		? cleanedInput
		: hasMathLikeContent(cleanedInput)
			? autoLatexifyMathLikeText(cleanedInput)
			: cleanedInput;

	const repairedContent = ensureMathWrapped(normalizeMathDelimiters(repairBrokenLatexFragments(normalizedContent)));

	const renderedHtml = React.useMemo(() => {
		try {
			return processContent(repairedContent);
		} catch (error) {
			return '';
		}
	}, [repairedContent]);

	const renderedHtmlText = String(renderedHtml || '');
	const renderedHtmlHasMath = /katex|mjx-container|math-inline|math-display|aria-label="Math formula"/i.test(renderedHtmlText);
	const sourceLooksMathLike = hasLatexContent(repairedContent) || hasMathLikeContent(repairedContent);
	const shouldUseRenderedHtml = Boolean(renderedHtmlText.trim()) && (renderedHtmlHasMath || !sourceLooksMathLike);
	const shouldUseMathJaxDirect = !shouldUseRenderedHtml && sourceLooksMathLike;

	React.useEffect(() => {
		if (shouldUseRenderedHtml) {
			return;
		}

		if (isMathDebugEnabled() && hasVectorArtifactHint(safeContent)) {
			console.info('[MathContentLayer][debug] vector pipeline', {
				raw: safeContent,
				rawCodePoints: toCodePointPreview(safeContent),
				cleanedInput,
				normalizedContent,
				repairedContent,
				shouldUseMathJaxDirect
			});
		}

		const element = containerRef.current;
		if (!element) return;

		const mathJax = (typeof window !== 'undefined' && window.MathJax) ? window.MathJax : null;
		if (!mathJax?.typesetPromise) return;

		mathJax.typesetPromise([element]).catch(() => {
			// Ignore MathJax runtime issues to avoid breaking UI rendering.
		});
	}, [safeContent, cleanedInput, normalizedContent, renderedHtml, repairedContent, shouldUseMathJaxDirect, shouldUseRenderedHtml]);

	if (!safeContent.trim()) {
		return null;
	}

	if (shouldUseRenderedHtml) {
		return (
			<div
				ref={containerRef}
				className={className}
				style={{ width: '100%', ...style }}
				dangerouslySetInnerHTML={{ __html: renderedHtml }}
			/>
		);
	}

	if (shouldUseMathJaxDirect) {
		return (
			<span
				ref={containerRef}
				className={className}
				style={{ whiteSpace: 'pre-wrap', width: '100%', ...style }}
			>
				{repairedContent}
			</span>
		);
	}

	return (
		<span className={className} style={{ whiteSpace: 'pre-wrap', ...style }}>
			{repairedContent}
		</span>
	);
};

export default MathContentLayer;
